import {useEffect, useState} from "react";
import { Star, Info, TrendingUp, HelpCircle, BarChart2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { upgradeRestaurantKarmaScore } from "../../api/api";
import {toast} from "../../hooks/use-toast"; // Import your endpoint

type KarmaScoreProps = {
    karmaPower: number;
    adjustedKarmaPower: number;
    timesChosen: number;
    restaurantId: number; // Add this prop
};

const karmaPackages = [
    { value: 1, price: 2.99, label: "Starter" },
    { value: 2, price: 4.99, label: "Bronze" },
    { value: 3, price: 7.49, label: "Silver" },
    { value: 4, price: 10.99, label: "Gold" },
    { value: 5, price: 14.99, label: "Platinum" },
    { value: 6, price: 19.99, label: "Diamond" },
    { value: 7, price: 25.99, label: "Elite" },
    { value: 8, price: 32.99, label: "Supreme" },
    { value: 9, price: 40.99, label: "Legend" },
    { value: 10, price: 49.99, label: "Mythic" },
];

export default function KarmaScore({
                                       karmaPower,
                                       adjustedKarmaPower,
                                       timesChosen,
                                       restaurantId,
                                   }: KarmaScoreProps) {
    const [showTooltip, setShowTooltip] = useState(false);
    const [confettiIdx, setConfettiIdx] = useState<number | null>(null);
    const [showTimesTooltip, setShowTimesTooltip] = useState(false);
    const [loading, setLoading] = useState<number | null>(null);
    const [localKarma, setLocalKarma] = useState({
        karmaPower,
        adjustedKarmaPower,
        timesChosen,
    });

    const availablePackages = karmaPackages.filter((pkg) => pkg.value > localKarma.karmaPower);

    const handleUpgrade = async (newKarma: number, idx: number) => {
        setLoading(idx);
        try {
            const res = await upgradeRestaurantKarmaScore(restaurantId, newKarma);
            if (res.error) {
                toast({
                    title: "Upgrade failed",
                    description: res.error,
                    variant: "default"
                });
                return;
            }

            // Payment session created: redirect user to Stripe checkout
            if (res.checkoutUrl) {
                window.open(res.checkoutUrl, "_blank"); // open in new tab
                // Or: window.location.href = res.checkoutUrl; // open in same tab
                toast({
                    title: "Almost done!",
                    description: "Complete your payment to upgrade Karma Score.",
                    variant: "default"
                });
            } else {
                toast({
                    title: "Payment Error",
                    description: "Could not initiate payment session. Try again.",
                    variant: "default"
                });
            }
        } catch (err) {
            toast({
                title: "Network Error",
                description: "Unable to process your request. Try again.",
                variant: "default"
            });
        } finally {
            setLoading(null);
        }
    };


    useEffect(() => {
        setLocalKarma({
            karmaPower,
            adjustedKarmaPower,
            timesChosen,
        });
    }, [karmaPower, adjustedKarmaPower, timesChosen, restaurantId]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <span className="flex items-center gap-2 text-gradient font-extrabold text-2xl">
                        <Star className="text-blue-200 animate-pulse" />
                        Karma Score & Visibility
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">

                {/* Stats Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-8">
                    {/* Karma Power - Pastel Blue */}
                    <div className="flex flex-col items-center bg-blue-50 dark:bg-blue-950 rounded-2xl shadow-2xl p-8 border-2 border-blue-100 dark:border-blue-800 scale-105 hover:scale-110 transition-transform duration-300">
                        <div className="flex items-center gap-3 mb-2">
                            <Star className="text-blue-200 w-14 h-14 animate-bounce drop-shadow-lg" />
                            <span className="text-6xl font-extrabold text-blue-800 dark:text-blue-100 drop-shadow-xl">
                                {localKarma.karmaPower}
                            </span>
                        </div>
                        <span className="text-lg font-bold text-blue-600 dark:text-blue-200 tracking-wide">
                            Your Karma Power
                        </span>
                    </div>

                    {/* Adjusted Karma - Pastel Green */}
                    <div
                        className="flex flex-col items-center bg-green-50 dark:bg-green-950 rounded-2xl shadow-2xl p-8 border-2 border-green-100 dark:border-green-800 relative scale-105 hover:scale-110 transition-transform duration-300"
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <TrendingUp className="text-green-200 w-14 h-14 animate-bounce drop-shadow-lg" />
                            <span className="text-6xl font-extrabold text-green-800 dark:text-green-100 drop-shadow-xl">
                                {localKarma.adjustedKarmaPower.toFixed(2)}
                            </span>
                            <HelpCircle className="w-5 h-5 text-green-300 ml-2 cursor-pointer" />
                        </div>
                        <span className="text-lg font-bold text-green-600 dark:text-green-200 tracking-wide">
                            Adjusted Karma
                        </span>
                        {showTooltip && (
                            <div className="absolute top-24 left-1/2 -translate-x-1/2 z-30 bg-white dark:bg-gray-900 shadow-2xl border px-6 py-4 text-base text-gray-800 dark:text-gray-100 rounded-2xl w-80 font-semibold">
                                <b>Adjusted Karma</b> is calculated from your Karma Power, customer feedback, and your response time.<br /><br />
                                The higher your Adjusted Karma, the more visible and trusted your restaurant becomes.<br />
                                <span className="font-bold text-green-700 dark:text-green-400">Fast responses and happy guests = more bookings!</span>
                            </div>
                        )}
                    </div>

                    {/* Times Chosen - Pastel Purple */}
                    <div
                        className="flex flex-col items-center bg-purple-100 dark:bg-purple-900 rounded-2xl shadow-2xl p-8 border-2 border-purple-200 dark:border-purple-800 scale-105 hover:scale-110 transition-transform duration-300 relative"
                        onMouseEnter={() => setShowTimesTooltip(true)}
                        onMouseLeave={() => setShowTimesTooltip(false)}
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <BarChart2 className="text-purple-400 w-14 h-14 animate-bounce drop-shadow-lg" />
                            <span className="text-6xl font-extrabold text-purple-900 dark:text-purple-100 drop-shadow-xl">
                                {localKarma.timesChosen}
                            </span>
                            <HelpCircle className="w-5 h-5 text-green-300 ml-2 cursor-pointer" />
                        </div>
                        <span className="text-lg font-bold text-purple-700 dark:text-purple-200 tracking-wide">
                            Times Chosen
                        </span>
                        {showTimesTooltip && (
                            <div className="absolute top-24 left-1/2 -translate-x-1/2 z-30 bg-white dark:bg-gray-900 shadow-2xl border px-6 py-4 text-base text-gray-800 dark:text-gray-100 rounded-2xl w-80 font-semibold">
                                Every time your restaurant is picked, your <span className="text-green-700 dark:text-green-300 font-bold">“Times Chosen”</span> increases.<br /><br />
                                <span className="font-bold text-green-800 dark:text-green-100">Higher Karma Score means you are picked more often, especially when users filter or search.</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* How Karma Score Works */}
                <div className="bg-gradient-to-r from-blue-50 via-green-50 to-purple-50 dark:from-blue-950 dark:via-green-950 dark:to-purple-950 border-l-4 border-blue-200 px-4 py-5 rounded-xl text-base text-blue-900 dark:text-blue-200 font-semibold flex items-start gap-3 shadow">
                    <Info className="mt-1 w-6 h-6 text-blue-200 animate-spin-slow" />
                    <div>
                        <b className="text-blue-900 dark:text-blue-300">How does Karma Score work and why upgrade?</b><br />
                        Karma Score directly affects how often your restaurant is shown to users.<br />
                        The higher your Karma Score, the higher your chance of being picked in user searches and recommendations.<br /><br />
                        <b>Adjusted Karma</b> is calculated from your base Karma Power, customer feedback, and your response performance.<br />
                        The more positive feedback and faster responses, the better your Adjusted Karma.<br /><br />
                        <span className="text-primary font-bold underline underline-offset-2">Upgrade your Karma Score to boost your visibility, climb the rankings, and win more bookings!</span>
                    </div>
                </div>

                {/* Growth Tips */}
                <div>
                    <div className="flex items-center gap-2 text-blue-400 dark:text-blue-200 font-semibold mb-2">
                        <Info className="w-5 h-5 animate-spin-slow" />
                        How to grow your Adjusted Karma:
                    </div>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-200 text-base ml-2 space-y-1 font-medium">
                        <li>Deliver <span className="text-green-700 dark:text-green-300 font-bold">excellent guest experiences</span> for positive feedback</li>
                        <li>Respond to bookings <span className="text-blue-700 dark:text-blue-300 font-bold">as fast as possible</span></li>
                        <li>Keep <span className="text-purple-700 dark:text-purple-300 font-bold">cancellations and no-shows low</span></li>
                        <li><span className="text-primary font-bold">Upgrade your Karma Score</span> below for an instant base boost</li>
                    </ul>
                </div>

                {/* Package Upgrade */}
                <div>
                    <div className="mb-4 text-xl font-extrabold text-gradient text-center animate-pulse">
                        <span className="bg-gradient-to-r from-purple-200 via-blue-200 to-green-200 bg-clip-text text-transparent">
                            🚀 Upgrade your Karma Score 🚀
                        </span>
                    </div>
                    {availablePackages.length === 0 ? (
                        <div className="py-8 text-center text-green-700 font-bold text-2xl flex items-center justify-center gap-2 animate-bounce">
                            <Star className="text-blue-200 w-7 h-7" />
                            Max Karma Score reached!
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-6 justify-center">
                            {availablePackages.map((pkg, idx) => (
                                <div
                                    key={pkg.value}
                                    className={`relative flex flex-col items-center border-2 rounded-2xl p-6 bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-gray-800 dark:via-blue-950 dark:to-purple-950 min-w-[150px] cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300 ${confettiIdx === idx ? "animate-shake" : ""}`}
                                    onMouseEnter={() => setConfettiIdx(idx)}
                                    onMouseLeave={() => setConfettiIdx(null)}
                                >
                                    {/* Confetti burst (CSS) */}
                                    {confettiIdx === idx && (
                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
                                            <span className="confetti"></span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-1 mb-2">
                                        <Star className="text-blue-200 w-6 h-6 animate-bounce" />
                                        <span className="text-2xl font-bold text-blue-700 dark:text-blue-200">{pkg.value}</span>
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">{pkg.label}</div>
                                    <div className="font-bold text-xl mb-3 text-purple-400 dark:text-purple-300">
                                        €{pkg.price.toFixed(2)}
                                    </div>
                                    <Button
                                        className="w-full bg-gradient-to-r from-blue-200 via-green-200 to-purple-200 text-gray-900 font-bold text-lg slow-pulse hover:scale-110 transition-all duration-300 shadow-lg"
                                        onClick={() => handleUpgrade(pkg.value, idx)}
                                        disabled={loading === idx}
                                    >
                                        {loading === idx ? "Upgrading..." : "Upgrade"}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="text-xs mt-5 text-gray-500 dark:text-gray-400 text-center">
                        Upgrades apply instantly. <span className="text-primary font-bold">Higher Karma = higher ranking.</span>
                        <br />
                        Adjusted Karma updates with every upgrade and every guest review.
                    </div>
                </div>
            </CardContent>
            {/* Confetti CSS and animations */}
            <style>{`
              .confetti {
                display: block;
                width: 80px;
                height: 20px;
                background: none;
                position: absolute;
                top: -18px;
                left: 50%;
                transform: translateX(-50%);
                pointer-events: none;
              }
              .confetti:after {
                content: "";
                display: block;
                width: 80px;
                height: 0;
                border-bottom: 20px dotted #c7d2fe; /* pastel blue */
                border-radius: 100px;
                opacity: 0.5;
                animation: confetti-burst 0.8s linear;
              }
              @keyframes confetti-burst {
                0% { opacity: 0.5; transform: scaleY(1) scaleX(1);}
                40% { opacity: 1; transform: scaleY(2) scaleX(1.4);}
                100% { opacity: 0; transform: scaleY(0.8) scaleX(2);}
              }
              .animate-shake {
                animation: shake 0.7s cubic-bezier(.36,.07,.19,.97) both;
              }
              @keyframes shake {
                10%, 90% { transform: translateX(-2px);}
                20%, 80% { transform: translateX(2px);}
                30%, 50%, 70% { transform: translateX(-4px);}
                40%, 60% { transform: translateX(4px);}
              }
              @keyframes slow-pulse {
                  0%, 100% { transform: scale(1);}
                  50% { transform: scale(1.05);}
              }
              .slow-pulse {
                  animation: slow-pulse 2.8s cubic-bezier(0.4,0,0.2,1) infinite;
              }
              .text-gradient {
                background: linear-gradient(90deg, #c7d2fe 0%, #bbf7d0 40%, #ddd6fe 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
              }
              .animate-spin-slow {
                animation: spin 4s linear infinite;
              }
            `}</style>
        </Card>
    );
}
